import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  IconButton,
  Box,
  Chip,
  Alert,
  Avatar,
  InputAdornment,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import axios from '../utils/axios';

const iconCategories = [
  { value: 'general', label: 'General' },
  { value: 'business', label: 'Business' },
  { value: 'social', label: 'Social' },
  { value: 'tech', label: 'Technology' },
  { value: 'education', label: 'Education' },
  { value: 'health', label: 'Health' },
  { value: 'finance', label: 'Finance' },
  { value: 'communication', label: 'Communication' },
];

const iconTypes = [
  { value: 'material-icon', label: 'Material Icon' },
  { value: 'static-image', label: 'Static Image' },
];

const iconSizes = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
];

// Popular Material Icons for quick selection
const popularMaterialIcons = [
  'home', 'person', 'email', 'phone', 'location_on', 'work', 'school', 'favorite',
  'star', 'settings', 'search', 'menu', 'close', 'check', 'add', 'edit',
  'delete', 'arrow_forward', 'arrow_back', 'expand_more', 'expand_less',
  'calendar_today', 'access_time', 'notifications', 'account_circle',
  'shopping_cart', 'payment', 'security', 'visibility', 'download',
  'upload', 'share', 'print', 'save', 'help', 'info', 'warning',
];

const IconManagement = () => {
  const [icons, setIcons] = useState([]);
  const [filteredIcons, setFilteredIcons] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingIcon, setEditingIcon] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [tabValue, setTabValue] = useState(0);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    category: 'general',
    type: 'material-icon',
    iconCode: '',
    imageUrl: '',
    color: '#000000',
    size: 'medium',
    isActive: true,
    tags: [],
    order: 0,
  });

  useEffect(() => {
    fetchIcons();
  }, []);

  useEffect(() => {
    filterIcons();
  }, [icons, searchTerm, selectedCategory, selectedType]);

  const fetchIcons = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/icons');
      setIcons(response.data.data);
    } catch (error) {
      setError('Failed to fetch icons');
    } finally {
      setLoading(false);
    }
  };

  const filterIcons = () => {
    let filtered = [...icons];
    
    if (searchTerm) {
      filtered = filtered.filter(icon => 
        icon.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        icon.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        icon.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(icon => icon.category === selectedCategory);
    }
    
    if (selectedType !== 'all') {
      filtered = filtered.filter(icon => icon.type === selectedType);
    }
    
    setFilteredIcons(filtered);
  };

  const handleOpen = (icon = null) => {
    if (icon) {
      setEditingIcon(icon);
      setFormData({
        name: icon.name,
        displayName: icon.displayName,
        category: icon.category,
        type: icon.type,
        iconCode: icon.iconCode || '',
        imageUrl: icon.imageUrl || '',
        color: icon.color || '#000000',
        size: icon.size,
        isActive: icon.isActive,
        tags: icon.tags || [],
        order: icon.order,
      });
    } else {
      setEditingIcon(null);
      setFormData({
        name: '',
        displayName: '',
        category: 'general',
        type: 'material-icon',
        iconCode: '',
        imageUrl: '',
        color: '#000000',
        size: 'medium',
        isActive: true,
        tags: [],
        order: icons.length,
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingIcon(null);
    setSelectedFile(null);
    setError('');
  };

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleTagsChange = (e) => {
    const tags = e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag);
    setFormData(prev => ({ ...prev, tags }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');
      
      let finalFormData = { ...formData };
      
      // If uploading a file for static image
      if (formData.type === 'static-image' && selectedFile) {
        try {
          const uploadedUrl = await uploadFile(selectedFile);
          finalFormData.imageUrl = uploadedUrl;
        } catch (uploadError) {
          setError('Failed to upload image file');
          return;
        }
      }
      
      if (editingIcon) {
        await axios.put(`/icons/${editingIcon._id}`, finalFormData);
        setSuccess('Icon updated successfully!');
      } else {
        await axios.post('/icons', finalFormData);
        setSuccess('Icon created successfully!');
      }

      await fetchIcons();
      handleClose();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to save icon');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this icon?')) {
      try {
        await axios.delete(`/icons/${id}`);
        setSuccess('Icon deleted successfully!');
        await fetchIcons();
      } catch (error) {
        setError('Failed to delete icon');
      }
    }
  };

  const handleToggleActive = async (icon) => {
    try {
      await axios.put(`/icons/${icon._id}`, {
        ...icon,
        isActive: !icon.isActive,
      });
      await fetchIcons();
    } catch (error) {
      setError('Failed to update icon status');
    }
  };

  const renderIcon = (icon) => {
    if (icon.type === 'material-icon') {
      return (
        <Box 
          component="span" 
          className="material-icons" 
          sx={{ 
            fontSize: icon.size === 'small' ? 20 : icon.size === 'large' ? 40 : 30,
            color: icon.color 
          }}
        >
          {icon.iconCode}
        </Box>
      );
    } else {
      return (
        <Avatar 
          src={icon.imageUrl} 
          sx={{ 
            width: icon.size === 'small' ? 20 : icon.size === 'large' ? 40 : 30,
            height: icon.size === 'small' ? 20 : icon.size === 'large' ? 40 : 30,
          }}
        >
          <ImageIcon />
        </Avatar>
      );
    }
  };

  const handleQuickIconSelect = (iconCode) => {
    setFormData(prev => ({ 
      ...prev, 
      iconCode,
      name: iconCode.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase(),
      displayName: iconCode.charAt(0).toUpperCase() + iconCode.slice(1).replace(/_/g, ' ')
    }));
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type)) {
        setError('Please select a valid image file (JPEG, PNG, GIF, or SVG)');
        return;
      }
      
      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        setError('File size must be less than 5MB');
        return;
      }
      
      setSelectedFile(file);
      
      // Auto-generate name and display name from filename
      const fileName = file.name.split('.')[0];
      const cleanName = fileName.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
      const displayName = fileName.replace(/[_-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      
      setFormData(prev => ({
        ...prev,
        name: cleanName,
        displayName: displayName,
        imageUrl: '' // Will be set after upload
      }));
    }
  };

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('image', file);
    
    try {
      setUploadingFile(true);
      const response = await axios.post('/upload/single', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return response.data.data.url;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to upload file');
    } finally {
      setUploadingFile(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Icon Library Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
        >
          Add Icon
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search icons..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 200 }}
        />
        
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            label="Category"
          >
            <MenuItem value="all">All Categories</MenuItem>
            {iconCategories.map((cat) => (
              <MenuItem key={cat.value} value={cat.value}>
                {cat.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            label="Type"
          >
            <MenuItem value="all">All Types</MenuItem>
            {iconTypes.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Icons Grid */}
      <Grid container spacing={2}>
        {filteredIcons.map((icon) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={icon._id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                  {renderIcon(icon)}
                </Box>
                <Typography variant="h6" component="h3" noWrap>
                  {icon.displayName}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {icon.name}
                </Typography>
                <Chip 
                  label={icon.category} 
                  size="small" 
                  variant="outlined"
                  sx={{ mt: 1 }}
                />
                <Box sx={{ mt: 1 }}>
                  <Chip 
                    label={icon.isActive ? 'Active' : 'Inactive'}
                    color={icon.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
              </CardContent>
              <CardActions sx={{ justifyContent: 'space-between' }}>
                <Box>
                  <IconButton
                    size="small"
                    onClick={() => handleOpen(icon)}
                    color="primary"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(icon._id)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={icon.isActive}
                      onChange={() => handleToggleActive(icon)}
                      size="small"
                    />
                  }
                  label=""
                />
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {editingIcon ? 'Edit Icon' : 'Add New Icon'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                name="name"
                label="Icon Name"
                value={formData.name}
                onChange={handleInputChange}
                required
                disabled={!!editingIcon}
                helperText="Unique identifier (cannot be changed after creation)"
              />
              
              <TextField
                name="displayName"
                label="Display Name"
                value={formData.displayName}
                onChange={handleInputChange}
                required
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl required sx={{ flex: 1 }}>
                  <InputLabel>Category</InputLabel>
                  <Select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    label="Category"
                  >
                    {iconCategories.map((cat) => (
                      <MenuItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl required sx={{ flex: 1 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    label="Type"
                  >
                    {iconTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl sx={{ flex: 1 }}>
                  <InputLabel>Size</InputLabel>
                  <Select
                    name="size"
                    value={formData.size}
                    onChange={handleInputChange}
                    label="Size"
                  >
                    {iconSizes.map((size) => (
                      <MenuItem key={size.value} value={size.value}>
                        {size.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {formData.type === 'material-icon' && (
                <Box>
                  <TextField
                    name="iconCode"
                    label="Material Icon Code"
                    value={formData.iconCode}
                    onChange={handleInputChange}
                    required={formData.type === 'material-icon'}
                    helperText="Material icon name (e.g., home, person, email)"
                  />
                  
                  {/* Quick Icon Selection */}
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Quick Select Popular Icons:
                    </Typography>
                    <Tabs
                      value={tabValue}
                      onChange={(e, newValue) => setTabValue(newValue)}
                      variant="scrollable"
                      scrollButtons="auto"
                    >
                      <Tab label="Popular" />
                    </Tabs>
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1, maxHeight: 200, overflow: 'auto' }}>
                      {popularMaterialIcons.map((iconCode) => (
                        <Button
                          key={iconCode}
                          variant={formData.iconCode === iconCode ? 'contained' : 'outlined'}
                          size="small"
                          onClick={() => handleQuickIconSelect(iconCode)}
                          startIcon={
                            <Box component="span" className="material-icons" sx={{ fontSize: 16 }}>
                              {iconCode}
                            </Box>
                          }
                        >
                          {iconCode}
                        </Button>
                      ))}
                    </Box>
                  </Box>
                </Box>
              )}

              {formData.type === 'static-image' && (
                <Box>
                  <TextField
                    name="imageUrl"
                    label="Image URL"
                    value={formData.imageUrl}
                    onChange={handleInputChange}
                    required={formData.type === 'static-image' && !selectedFile}
                    helperText="Full URL to the image file or upload a file below"
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Or upload an image:
                    </Typography>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<ImageIcon />}
                      disabled={uploadingFile}
                    >
                      {selectedFile ? selectedFile.name : 'Choose File'}
                      <input
                        type="file"
                        hidden
                        accept="image/jpeg,image/jpg,image/png,image/gif,image/svg+xml"
                        onChange={handleFileSelect}
                      />
                    </Button>
                    {selectedFile && (
                      <Button
                        size="small"
                        onClick={() => {
                          setSelectedFile(null);
                          setFormData(prev => ({ ...prev, imageUrl: '' }));
                        }}
                      >
                        Remove
                      </Button>
                    )}
                  </Box>
                  
                  {selectedFile && (
                    <Box sx={{ p: 2, border: '1px dashed grey', borderRadius: 1, mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Selected File:
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Name: {selectedFile.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Size: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Type: {selectedFile.type}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  name="color"
                  label="Color"
                  type="color"
                  value={formData.color}
                  onChange={handleInputChange}
                  sx={{ flex: 1 }}
                />
                
                <TextField
                  name="order"
                  label="Display Order"
                  type="number"
                  value={formData.order}
                  onChange={handleInputChange}
                  sx={{ flex: 1 }}
                />
              </Box>

              <TextField
                name="tags"
                label="Tags"
                value={formData.tags.join(', ')}
                onChange={handleTagsChange}
                helperText="Comma-separated tags for easier searching"
                multiline
                rows={2}
              />

              <FormControlLabel
                control={
                  <Switch
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                }
                label="Active"
              />

              {/* Preview */}
              {(formData.iconCode || formData.imageUrl || selectedFile) && (
                <Box sx={{ textAlign: 'center', p: 2, border: '1px dashed grey', borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Preview:</Typography>
                  {formData.type === 'material-icon' && formData.iconCode && (
                    <Box 
                      component="span" 
                      className="material-icons" 
                      sx={{ 
                        fontSize: formData.size === 'small' ? 20 : formData.size === 'large' ? 40 : 30,
                        color: formData.color 
                      }}
                    >
                      {formData.iconCode}
                    </Box>
                  )}
                  {formData.type === 'static-image' && (formData.imageUrl || selectedFile) && (
                    <Avatar 
                      src={selectedFile ? URL.createObjectURL(selectedFile) : formData.imageUrl} 
                      sx={{ 
                        width: formData.size === 'small' ? 20 : formData.size === 'large' ? 40 : 30,
                        height: formData.size === 'small' ? 20 : formData.size === 'large' ? 40 : 30,
                        mx: 'auto'
                      }}
                    >
                      <ImageIcon />
                    </Avatar>
                  )}
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={loading || uploadingFile}>
              {uploadingFile ? 'Uploading...' : loading ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
};

export default IconManagement;